import { defineStore } from "pinia";
import { useContentsStore } from "./ContentsStore";
import { DistributionRepository } from "~/repositories/DistributionRepository";
import { DistributionInfo } from "~/entities/DistributionInfo";
import type { RelatedContentsItem } from "~/entities/RelatedContentsItem";
import type { IDistributionRepository } from "~/interfaces/IDistributionRepository";

export const useDistributionStore = defineStore("DistributionStore", () => {
  const repository: IDistributionRepository = new DistributionRepository();
  const distributionInfo = ref<DistributionInfo>(new DistributionInfo());
  const relatedContents = ref<RelatedContentsItem[]>([]);

  // 進捗率（マルチパートアップロード時のみ使用）
  const uploadProgress = ref<{ now: number; max: number } | null>(null);

  const reset = () => {
    distributionInfo.value = new DistributionInfo();
    relatedContents.value = [];
  };

  const fetchRelatedContents = async (contentsId: string) => {
    relatedContents.value = await repository.getRelatedContents(contentsId);
  };

  const uploadContentFile = async (file: File) => {
    try {
      if (featureMultiPartUpload()) {
        uploadProgress.value = null;
        const res = await repository.uploadContentFile(file, (now: number, max: number) => {
          uploadProgress.value = { now, max };
        });
        distributionInfo.value.contentFile.fileKey = res.fileKey;
      } else {
        const res = await repository.uploadContentFile(file);
        distributionInfo.value.contentFile.fileKey = res.fileKey;
      }
    } finally {
      uploadProgress.value = null;
    }
  };

  const uploadArticleImageFile = async (file: File) => {
    return await repository.uploadContentFile(file);
  };

  const uploadThumbnailFile = async (file: File, trimming: boolean = true) => {
    const convertedFile = await convertContentImage(file, trimming);
    const res = await repository.uploadThumbnailFile(convertedFile);
    distributionInfo.value.thumbnailFile.fileKey = res.fileKey;
  };

  const setMoviePlayTimeSec = (moviePlayTimeSec: number) => {
    distributionInfo.value.moviePlayTimeSec = moviePlayTimeSec;
  };

  const postDistribution = async (): Promise<{ contentsId: string; contentsGroupKey: string }> => {
    const res = await repository.postDistribution(distributionInfo.value);
    return res;
  };

  const putDistribution = async () => {
    await repository.putDistribution(distributionInfo.value);
  };

  const deleteDistribution = async () => {
    await repository.deleteDistribution(distributionInfo.value);
  };

  const recoverDistribution = async () => {
    await repository.recoverDistribution(distributionInfo.value);
  };
  const getSignedUrl = async (fileKey: string) => {
    return await repository.getSignedUrl(fileKey);
  };

  // 関連コンテンツの詳細を取得してlimitedPlanPricesを統合
  const fetchRelatedContentsDetails = async (
    relatedContents: RelatedContentsItem[],
  ): Promise<{ planId: string; planPriceId: number }[]> => {
    const contentsStore = useContentsStore();
    const allLimitedPlanPrices: { planId: string; planPriceId: number }[] = [];

    for (const relatedContent of relatedContents) {
      if (relatedContent.isDeleted) continue;

      try {
        // ContentsStoreの汎用getDetailを使用
        const contentInfo = await contentsStore.getDetail<{ limitedPlanPriceIds?: number[] }>(
          relatedContent.contentsId,
        );

        if (contentInfo?.limitedPlanPriceIds) {
          const contentLimitedPrices = contentInfo.limitedPlanPriceIds.map(planPriceId => ({
            planId: relatedContent.planId,
            planPriceId: planPriceId,
          }));
          allLimitedPlanPrices.push(...contentLimitedPrices);
        }
      } catch (error) {
        sentryErrorLog(`コンテンツ ${relatedContent.contentsId} の詳細取得に失敗しました:`, error);
      }
    }

    return allLimitedPlanPrices;
  };

  async function updateMultiContents(params: {
    distributionInfo: DistributionInfo;
    selectedPlanIds: string[];
    prevSelectedPlans: string[];
    relatedContents: any[];
    limitedPlanPrices: { planId: string; planPriceId: number }[];
  }) {
    const { distributionInfo, selectedPlanIds, prevSelectedPlans, relatedContents, limitedPlanPrices } = params;
    // 現在のplanIDとcontentsIdをバックアップ
    const currentPlanId = distributionInfo.planId;
    const currentContentsId = distributionInfo.contentsId;

    const successPlanIds: string[] = [];
    const allUpdatedPlanIds: string[] = [];

    try {
      // 更新するコンテンツを取得
      const updatedPlanIds = selectedPlanIds.filter(planId => prevSelectedPlans.includes(planId));
      const updatedContents = relatedContents.filter(c => updatedPlanIds.includes(c.planId));

      // 追加するplanIdを取得
      const addedPlanIds = selectedPlanIds.filter(planId => !relatedContents.some(c => c.planId === planId));

      // 削除するコンテンツを取得
      const deletedPlanIds = prevSelectedPlans.filter(planId => !selectedPlanIds.includes(planId));
      const deletedContents = relatedContents.filter(c => deletedPlanIds.includes(c.planId));

      // 再度追加するplanを取得
      const recoveredPlanIds = selectedPlanIds.filter(planId =>
        relatedContents.some(c => c.planId === planId && c.isDeleted),
      );
      const recoveredContents = relatedContents.filter(c => recoveredPlanIds.includes(c.planId));

      allUpdatedPlanIds.push(...updatedPlanIds, ...addedPlanIds, ...deletedPlanIds, ...recoveredPlanIds);

      if (allUpdatedPlanIds.length === 0) {
        // 更新するplanがない場合はエラーとする
        throw new Error("更新するplanがありません");
      }

      // 各操作を順次実行
      // 更新処理
      for (const content of updatedContents) {
        distributionInfo.planId = content.planId;
        distributionInfo.contentsId = content.contentsId;
        // 各講座の限定公開プランIDを設定
        distributionInfo.limitedPlanPriceIds = limitedPlanPrices
          .filter(p => p.planId === content.planId)
          .map(p => p.planPriceId);
        await putDistribution();
        successPlanIds.push(content.planId);
      }

      // 追加処理
      for (const planId of addedPlanIds) {
        distributionInfo.planId = planId;
        // 各講座の限定公開プランIDを設定
        distributionInfo.limitedPlanPriceIds = limitedPlanPrices
          .filter(p => p.planId === planId)
          .map(p => p.planPriceId);
        const res = await postDistribution();
        successPlanIds.push(planId);
        if (!distributionInfo.contentsGroupKey && res?.contentsGroupKey) {
          // グループキーがない場合は、新規作成した記事のグループキーを設定
          distributionInfo.contentsGroupKey = res.contentsGroupKey;
        }
      }

      // 削除処理
      for (const content of deletedContents) {
        distributionInfo.planId = content.planId;
        distributionInfo.contentsId = content.contentsId;
        await deleteDistribution();
        successPlanIds.push(content.planId);
      }

      // 再度追加処理
      for (const content of recoveredContents) {
        distributionInfo.planId = content.planId;
        distributionInfo.contentsId = content.contentsId;
        // 各講座の限定公開プランIDを設定
        distributionInfo.limitedPlanPriceIds = limitedPlanPrices
          .filter(p => p.planId === content.planId)
          .map(p => p.planPriceId);
        await recoverDistribution();
        successPlanIds.push(content.planId);
      }

      return {
        success: true,
        successPlanIds,
        allUpdatedPlanIds,
      };
    } catch (error) {
      sentryErrorLog(
        `updateMultiContents error success: ${successPlanIds.join(",")} planIds: ${allUpdatedPlanIds.join(",")}`,
        error,
      );
      return {
        success: false,
        successPlanIds,
        allUpdatedPlanIds,
      };
    } finally {
      // planIDとcontentsIdをリストア
      distributionInfo.planId = currentPlanId;
      distributionInfo.contentsId = currentContentsId;
    }
  }

  return {
    distributionInfo,
    relatedContents,
    uploadProgress,
    reset,
    fetchRelatedContents,
    fetchRelatedContentsDetails,
    uploadContentFile,
    uploadArticleImageFile,
    uploadThumbnailFile,
    setMoviePlayTimeSec,
    postDistribution,
    putDistribution,
    deleteDistribution,
    recoverDistribution,
    getSignedUrl,
    updateMultiContents,
  };
});
